import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { Course, CourseResponse } from '@edtech-ui/core/models/shared-resources/shared-resource.model';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { CourseManagementService } from '@edtech-ui/core/services/course-management/course-management.service';
import { ToastrService } from 'ngx-toastr';
import { finalize, Subscription } from 'rxjs';

@Component({
	selector: 'app-student-classroom-course',
	standalone: true,
	imports: [CommonModule,MatProgressSpinnerModule],
	templateUrl: './student-classroom-course.component.html',
	styleUrl: './student-classroom-course.component.scss'
})
export class StudentClassroomCourseComponent {
	classroomId: number | null = null;
	courseResources: Course[] = [];
	private routerSub: Subscription | null = null;
	private lastFetchedClassroomId: number | null = null;
	private studentId: string | null = null;
	isLoading = false;

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private _courseService: CourseManagementService,
		private _toastr: ToastrService,
		private _authService: AuthServiceService
	) { }

	ngOnInit() {
		this.studentId = this._authService.getUserId();

		if (!this.studentId) {
			this._toastr.error('Student ID not found. Please log in again.');
			this.router.navigate(['/login']);
			return;
		}

		this.route.parent?.paramMap.subscribe(params => {
			const id = params.get('id');
			if (id) {
				this.classroomId = +id;
				if (this.classroomId !== this.lastFetchedClassroomId) {
					this.fetchCourses();
					this.lastFetchedClassroomId = this.classroomId;
				}
			} else {
				this._toastr.error('Classroom ID not found in the URL.');
				this.router.navigate(['/student-dashboard/classrooms']);
			}
		});
	}

	fetchCourses() {
		if (!this.classroomId) {
			this._toastr.error('Classroom ID is missing.');
			this.router.navigate(['/student-dashboard/classrooms']);
			return;
		}
		this.isLoading = true;
		this._courseService.getStudentCourses(this.classroomId, 1, 10, 10, 'desc', 'all').pipe(
			finalize (()=>{
				this.isLoading = false;
			})
		).subscribe({
			next: (response: CourseResponse) => {
				if (response.Success && response.Data) {
					this.courseResources = response.Data.data || [];
				} else {
					this._toastr.error(response.Message || 'Failed to fetch courses.');
				}
			},
			error: () => {
				this._toastr.error('Failed to fetch courses due to a server error.');
			}
		});
	}

	formatDate(dateStr: string): string {
		const date = new Date(dateStr);
		const day = date.getDate().toString().padStart(2, '0');
		const month = date.toLocaleString('default', { month: 'short' });
		const year = date.getFullYear();
		return `${day}-${month}-${year}`;
	}

	getCourseIcon(title: string): string {
		switch (title) {
			case 'Web Development Basics':
				return 'assets/images/AI_generated.svg';
			case 'Java Fundamentals':
				return 'assets/images/manual_created.svg';
			default:
				return 'assets/images/manual_created.svg';
		}
	}

	viewCourse(course: Course) {
		if (!this.classroomId || !course?.course_id) {
			this._toastr.error('Unable to view course: Missing classroom or course ID.');
			return;
		}
		if (course.is_ai) {
			this.router.navigate(['/student-dashboard/course-view', this.classroomId, course.course_id]);
		} else {
			this.router.navigate(['/student-dashboard/manual-course-view', this.classroomId, course.course_id]);
		}
	}

	calculateDuration(start: string, end: string): string {
		if (!start || !end) return 'N/A';
		const diffMs = new Date(end).getTime() - new Date(start).getTime();
		const minutes = Math.floor(diffMs / 60000);
		return `${minutes} min`;
	}

	ngOnDestroy() {
		if (this.routerSub) {
			this.routerSub.unsubscribe();
		}
	}
}